/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useMemo } from "react";
import { getCloudinaryUrl } from "@swiggy-private/js-utils";
import type { NRErrorInfo } from "@swiggy-private/js-utils";
import { ICartItem } from "@swiggy-private/dls-web-recipe-common-web/dist/components/FoodItem/types";
import { ResponseContext, ScreenNames, IApiConfigs } from "@swiggy-private/portal-dineout-context";
import { customWidgetTypeHandler } from "./components/CustomPLController";
import { TEXTS } from "./constants";
import { filterMenuData } from "./helper";
import { useCrossSellRenderer } from "./hooks/useCrossSellRenderer";
import { CustomWidgetTypes } from "./types";
import BaseMenu from "../BaseMenu/BaseMenu";
import { IBaseMenuConfig } from "../BaseMenu/types";
import { useAnalytics } from "../hooks";

interface IMenuProps {
    ifCartItemAvailable: boolean;
    goToCart: VoidFunction;
    onInvalidWidget: (widgetName?: string) => void;
    onWidgetError: (error: NRErrorInfo, widgetName?: string) => void;
    onEmptyPage: () => void;
    onMenuLoad?: () => void;
    impressionAnalytics?: (clickObject: Record<string, any>) => void;
    clickAnalytics?: (clickObject: Record<string, any>) => void;
    genericClickHandler?: (card?: ICartItem, extraParams?: Record<string, any>) => void;
    /** API configs containing POPUP configuration - popup cards will be extracted and rendered automatically */
    popupConfigs?: IApiConfigs;
    /** Screen name for analytics */
    screenName: ScreenNames;
    /** Called when cross-sell items are confirmed visible — used to update frequency cap */
    onCrossSellShown?: (itemId: string) => void;
}

const Menu = ({
    clickAnalytics: defaultClickAnalytics,
    impressionAnalytics: defaultImpressionAnalytics,
    genericClickHandler,
    ifCartItemAvailable,
    goToCart,
    onInvalidWidget,
    onWidgetError,
    onEmptyPage,
    onMenuLoad,
    popupConfigs,
    screenName,
    onCrossSellShown,
}: IMenuProps) => {
    const { state } = useContext(ResponseContext);
    const { impressionAnalytics, clickAnalytics } = useAnalytics({
        screenName,
        defaultImpressionAnalytics,
        defaultClickAnalytics,
    });

    const { getCrossSellRenderer } = useCrossSellRenderer(onCrossSellShown, impressionAnalytics);

    const { filteredData, fabWidgetData } = useMemo(
        () =>
            filterMenuData({
                filteredData: state.menu.filteredData,
                initialData: state.menu.initialData,
                itemExpandImpression: clickAnalytics,
                getCrossSellRenderer,
            }),
        [state.menu.filteredData, state.menu.initialData, clickAnalytics, getCrossSellRenderer]
    );

    const menuConfig: IBaseMenuConfig = {
        // Data source
        cards: filteredData,
        fabWidgetData,
        popupConfigs,
        loading: state.menu.loading,
        emptyItemCard: state.menu.emptyItemCard,

        // Custom widget handling
        customWidgetTypeHandler,
        customWidgetTypes: Object.values(CustomWidgetTypes),

        // Analytics
        screenName,
        clickAnalytics,
        impressionAnalytics,
        // Handlers
        genericClickHandler,
        onInvalidWidget,
        onWidgetError,
        onEmptyPage,

        // UI Configuration
        showFloatingWidget: true,
        showBottomWidget: true,
        ifCartItemAvailable,
        goToCart,
        cartItemsCount:
            state.cart?.localCart?.cartItems?.reduce((total, item) => total + item.quantity, 0) ||
            0,

        // Empty state configuration
        emptyStateConfig: {
            heading: TEXTS.HEADING,
            description: TEXTS.DESCRIPTION,
            imgSrc:
                getCloudinaryUrl(
                    "portal/static-assets/images/digital-menu/all-theme-assets/no_results_final.png",
                    {
                        width: 220 * 2,
                        height: 215 * 2,
                    }
                ) || "",
        },
    };

    return <BaseMenu onMenuLoad={onMenuLoad} config={menuConfig} />;
};

export default React.memo(Menu);
